/**
 * Boxes 9 and 15 of the ICS 208HM-CG, which are one signature printed twice:
 * box 9 closes page 1 and box 15 closes page 2, the way the 202-CG reprints its
 * preparer in boxes 10 and 14.
 *
 * The FEMA template behind this base id prints no signature box at all, so the
 * slot is declared by `FAMILY_EXTRA_SLOTS` on the server rather than read off a
 * descriptor. Signing still happens on the version bar above.
 */

import type React from 'react';
import { useMemo } from 'react';

import { useFormVersionTip } from 'components/IAP/hooks/icsFormVersionHooks';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import {
  formatSignatureDateTime,
  parseWalaSignatures,
} from './AssignmentList/ics204SignatureSlots';
import { ICS208HM_BOX_LABELS } from './ics208hmBoxLabels';

export type Ics208HmSignatureBoxProps = {
  periodNumber: number;
};

const Line: React.FC<{ label: string; value: string; italic?: boolean }> = ({
  label,
  value,
  italic,
}) => (
  <div className={italic ? 'tw:italic' : undefined}>
    <span
      className={
        italic
          ? 'tw:not-italic tw:text-muted-foreground'
          : 'tw:text-muted-foreground'
      }
    >
      {label}:{' '}
    </span>
    {value}
  </div>
);

export const Ics208HmSignatureBox: React.FC<Ics208HmSignatureBoxProps> = ({
  periodNumber,
}) => {
  const { data: tip } = useFormVersionTip('ICS_208HM', periodNumber);
  const signature = useMemo(
    () => parseWalaSignatures(tip?.signatures)[0],
    [tip?.signatures]
  );

  return (
    <Ics204CardSection
      boxLabel={ICS208HM_BOX_LABELS.preparedBy}
      testId="ics208hm-signature-box"
      className="tw:mt-0"
    >
      {signature ? (
        <div className="tw:grid tw:gap-1 tw:text-sm">
          <Line label="Name" value={signature.name} />
          <Line
            label="Position / Title"
            // The roster seat the signer held, resolved by the server. Older
            // signatures predate it and fall back to the slot they filled.
            value={signature.positionTitle || signature.role}
          />
          <Line label="Signature" value={signature.name} italic />
          <Line
            label="Date / Time"
            value={formatSignatureDateTime(signature.signedAt)}
          />
        </div>
      ) : (
        <div className="tw:text-sm tw:text-muted-foreground">
          Not signed. Create a signed version above to sign this form. The same
          signature prints in boxes 9 and 15.
        </div>
      )}
    </Ics204CardSection>
  );
};

export default Ics208HmSignatureBox;
